/**
 * Reduce a free-form intent string to a SPARQL-IRI-safe `cogo:patternRef` token.
 *
 * `kg-repository.findExamples` embeds `q.pattern` into an IRI position
 * (`local-wasm-repository.ts:356-358`):
 *
 *     <https://cogo.xyz/instances/pattern/${pattern}>
 *
 * `sanitizeIri` upstream is permissive — it does NOT strip whitespace or
 * non-ASCII codepoints, so a pattern like `"회의 일정 관리 SaaS"` produces
 * an IRI containing literal spaces + Korean codepoints, which oxigraph
 * rejects with `expected IRI parsing failed`. The thrown error then
 * propagates as `kg-init-failed` (infra) and `routeChannel` refuses to
 * fall through to Channel 2.
 *
 * Channel 2's mock corpus picker already applies a kebab-case rule
 * (`mock-corpus-picker.ts:88`); this helper is the canonical version,
 * stricter on non-ASCII so that:
 *
 *  - ASCII intents survive (`"auth-form"` → `"auth-form"`)
 *  - Mixed intents keep their ASCII fragment (`"📝 Sample heading 빨갛게"` → `"sample-heading"`)
 *  - Pure non-ASCII intents collapse to empty (`"빨갛게 만들어줘"` → `""`),
 *    letting the caller short-circuit to `no-candidates` and let the
 *    router fall through to Channel 2 LLM generation.
 *
 * Output is lowercased kebab-case `[a-z0-9-]+` (or empty).
 *
 * Algorithm — order matters:
 *  1. trim whitespace
 *  2. strip non-printable / non-ASCII (Korean codepoints, emoji, control)
 *  3. collapse remaining whitespace runs into single `-`
 *  4. strip anything else not in `[a-zA-Z0-9-]` (punctuation)
 *  5. collapse repeated `-`
 *  6. trim leading/trailing `-`
 *  7. lowercase
 */
export function sanitizePatternToken(text: string): string {
  if (typeof text !== 'string') return ''
  return text
    .trim()
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}
